import { useEffect, useMemo, useRef, useState } from "react";
import {
  getSnapTradeInit,
  registerSnapTradeUser as registerSnapTradeUserAPI,
  getConnectionPortalUrl,
  getConnections,
  getConnectionDetail,
  deleteConnection as deleteConnectionAPI,
} from "../../../../api/SnapTrade";
import { addOrUpdateSyncSetting, runSyncSetting } from "../../../../api/AutoSync";
import { useAtomValue } from "jotai";
import { AccountAtom } from "../../../../store";
import { Brokers, SyncSetting } from "../../../../types";

export interface SnapTradeControllerProps {
  closeModal: () => void;
}

export const useSnapTradeController = (props: SnapTradeControllerProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncLoading, setIsSyncLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [snapTradeUserId, setSnapTradeUserId] = useState<string | null>(null);
  const [broker, setBroker] = useState<string>("");
  const [connections, setConnections] = useState<any[]>([]);
  const [selectedConnection, setSelectedConnection] = useState<any | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [connectModalOpen, setConnectModalOpen] = useState(false);
  const [loginLink, setLoginLink] = useState<string>("");
  const processedAuthorizationIdRef = useRef<string | null>(null);
  const accountState = useAtomValue(AccountAtom);
  const selectedPortfolioId = accountState?.selectedAccount?.id || 0;
  const selectedPortfolioName = accountState?.selectedAccount?.name || "";
  const hasRequiredSelections = useMemo(() => !!broker && !!selectedPortfolioId, [broker, selectedPortfolioId]);
  const [hasRunSync, setHasRunSync] = useState(false);
  const loadConnections = async () => {
    try {
      const list = await getConnections();
      if (list?.success && Array.isArray(list.data)) {
        setConnections(list.data);
      }
    } catch (e: any) {
      setErrorMessage(e?.message || "Failed to load connections.");
    }
  };

  const init = async () => {
    try {
      const { snapTradeUserId } = await getSnapTradeInit();
      if (snapTradeUserId) {
        setSnapTradeUserId(snapTradeUserId);
      } else {
        const res = await registerSnapTradeUserAPI();
        setSnapTradeUserId(res?.snapTradeUserId || null);
      }
      await loadConnections();
    } catch (e: any) {
      setErrorMessage(e?.message || "Failed to initialize integration.");
    }
  };

  useEffect(() => {
    init();
  }, []);

  const connect = async () => {
    try {
      setIsLoading(true);
      setErrorMessage("");
      setSuccessMessage("");
      if (!broker) {
        setErrorMessage("Please select a broker.");
        return;
      }
      if (!selectedPortfolioId) {
        setErrorMessage("Please select a portfolio to sync.");
        return;
      }
      const result = await getConnectionPortalUrl(broker || undefined);
      const url = result?.redirectURI;
      if (url) {
        processedAuthorizationIdRef.current = null;
        setLoginLink(url);
        setConnectModalOpen(true);
      } else {
        setErrorMessage("Could not generate connection portal URL.");
      }
    } catch (err: any) {
      setErrorMessage(err?.message || "Failed to start connection.");
    } finally {
      setIsLoading(false);
    }
  };

  const viewConnection = async (id: string) => {
    try {
      const detail = await getConnectionDetail(id);
      setSelectedConnection(detail?.data || null);
      setDetailOpen(true);
    } catch (e: any) {
      setErrorMessage(e?.message || "Failed to load connection detail");
    }
  };



  const deleteConnection = async (id: string) => {
    try {
      await deleteConnectionAPI(id);
      setConnections((prev) => prev.filter((x: any) => x.id !== id));
      setSuccessMessage("Connection deleted");
    } catch (e: any) {
      setErrorMessage(e?.message || "Failed to delete connection");
    }
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setSelectedConnection(null);
  };

  const handleConnectClose = () => {
    processedAuthorizationIdRef.current = null;
    setConnectModalOpen(false);
  };

  const saveSyncSetting = async (authorizationId: string) => {

    if(!hasRunSync) {
    setHasRunSync(true);

    try {
      if (!selectedPortfolioId) return;
      const payload: SyncSetting = {
        accountId: selectedPortfolioId,
        broker:  Brokers.SnapTrade,
        name: "Broker Auto Sync",
        settings: JSON.stringify({ provider: "SNAPTRADE", authorizationId, broker }),
      };
      await addOrUpdateSyncSetting(payload, selectedPortfolioId);
      // Immediately run a sync to fetch the latest trades
      try {
        setIsSyncLoading(true);
        setTimeout(async () => {
          try {
            await runSyncSetting(payload, selectedPortfolioId);
          } finally {
            setIsSyncLoading(false);
          }
        }, 10000);
      } catch {}
    } catch (e) {
      // non-fatal
    }
  }
  };

  const handleConnectSuccess = async (data?: any) => {
    try {
      const authorizationId: string | undefined =
        data?.authorizationId || data?.authorization_id || data?.authId;

      // Guard against duplicate success events from the iframe
      if (authorizationId) {
        if (processedAuthorizationIdRef.current === authorizationId) {
          return;
        }
        processedAuthorizationIdRef.current = authorizationId;
        await saveSyncSetting(authorizationId);
      }
      setSuccessMessage("Successfully connected. Sync will begin shortly.");
      setErrorMessage("");
      setConnectModalOpen(false);
      await loadConnections();
    } catch {}
  };

  const handleConnectError = (err?: any) => {
    setErrorMessage(err?.message || "Connection failed or was cancelled.");
    setSuccessMessage("");
  };

  // Listen for window messages from SnapTrade iframe
  useEffect(() => {
    const handleMessageEvent = (e: MessageEvent) => {
      if (!connectModalOpen) return;
      const data = (e && e.data) as any;
      if (!data) return;

      try {
        if (typeof data === "string") {
          if (data.startsWith("SUCCESS:")) {
            const authorizationId = data.split(":")[1];
            if (authorizationId) {
              handleConnectSuccess({ authorizationId });
            }
            return;
          }
          if (data.startsWith("ERROR:")) {
            handleConnectError({ message: data });
            setConnectModalOpen(false);
            return;
          }
          if (data === "CLOSED" || data === "CLOSE_MODAL" || data === "ABANDONED") {
            setConnectModalOpen(false);
            return;
          }
        }

        if (typeof data === "object") {
          if (data.status === "SUCCESS") {
            const authorizationId = data.authorizationId;
            if (authorizationId) {
              handleConnectSuccess({ authorizationId });
            }
            return;
          }
          if (data.status === "ERROR") {
            handleConnectError({ message: data?.detail || data?.errorCode || "Connection failed" });
            setConnectModalOpen(false);
            return;
          }
          if (data === "CLOSED" || data === "CLOSE_MODAL" || data === "ABANDONED") {
            setConnectModalOpen(false);
            return;
          }
        }
      } catch {}
    };

    window.addEventListener("message", handleMessageEvent, false);
    return () => window.removeEventListener("message", handleMessageEvent, false);
  }, [connectModalOpen]);

  return {
    // state
    isLoading,
    errorMessage,
    successMessage,
    snapTradeUserId,
    broker,
    connections,
    selectedConnection,
    detailOpen,
    connectModalOpen,
    loginLink,
    selectedPortfolioId,
    selectedPortfolioName,
    hasRequiredSelections,
    // actions
    setBroker,
    setErrorMessage,
    setSuccessMessage,
    isSyncLoading,
    connect,
    handleConnectClose,
    handleConnectSuccess,
    handleConnectError,
    viewConnection,
    deleteConnection,
    closeDetail,
    closeModal: props.closeModal,
  };
};

export type SnapTradeController = ReturnType<typeof useSnapTradeController>;


